import { ImageUploadDropzone } from "@dashboard/imports/components/ImageUploadDropzone/ImageUploadDropzone";
import { useTemplateDownload } from "@dashboard/imports/hooks/useTemplateDownload";
import { ImportType } from "@dashboard/imports/types";
import { IMPORT_SCAN_IMAGES_URL, IMPORT_TEMPLATE_URL } from "@dashboard/imports/urls";
import { Button, Text } from "@saleor/macaw-ui-next";
import React from "react";
import { FormattedMessage } from "react-intl";

import { messages } from "./messages";

interface FieldPreview {
  name: string;
  description: string;
  required: boolean;
}

interface PipelineStep {
  sheetName: string;
  displayName: string;
  position: number;
  dependsOn: string[];
  fieldPreview: FieldPreview[];
}

interface ImportPipelineChainProps {
  steps: PipelineStep[];
  onImport: (importType: ImportType) => void;
}

const STEP_IMPORT_TYPE_MAP: Record<string, ImportType> = {
  shipping_rates: ImportType.SHIPPING_RATES,
  categories: ImportType.CATEGORIES,
  attributes: ImportType.ATTRIBUTES,
  product_types: ImportType.PRODUCT_TYPES,
  product_type_attributes: ImportType.PRODUCT_TYPE_ATTRIBUTES,
  products: ImportType.PRODUCTS,
  variants: ImportType.PRODUCT_VARIANTS,
  channel_listings: ImportType.CHANNEL_LISTINGS,
  images: ImportType.IMAGES,
  collections: ImportType.COLLECTIONS,
  collection_products: ImportType.COLLECTION_PRODUCTS,
  // variant_channel_listings intentionally omitted — bundled in PRODUCT_VARIANTS
};

// Sheets that show no actions at all — info-only nodes in the chain.
const INFO_ONLY_SHEETS = new Set(["variant_channel_listings"]);

// Maps sheet name → backend import_type URL segment.
const SHEET_TO_TEMPLATE_TYPE: Record<string, string> = {
  shipping_rates: "shipping_rates",
  categories: "categories",
  attributes: "attributes",
  product_types: "product_types",
  product_type_attributes: "product_type_attributes",
  products: "products",
  variants: "product_variants",
  channel_listings: "channel_listings",
  variant_channel_listings: "variant_channel_listings",
  images: "images",
  collections: "collections",
  collection_products: "collection_products",
};

// Colour tokens
const COLOR_BORDER = "#e5e7eb";
const COLOR_ACCENT = "#2563eb";
const COLOR_ARROW = "#9ca3af";

const CARD_WIDTH = 240;
const CARD_GAP = 16; // vertical gap between branch cards
const COL_GAP = 48; // horizontal gap between columns (for connector lines)

// ---------------------------------------------------------------------------
// Single card
// ---------------------------------------------------------------------------

interface CardProps {
  step: PipelineStep;
  onImport: (importType: ImportType) => void;
  download: (url: string, filename: string) => void;
}

const Card = ({ step, onImport, download }: CardProps) => {
  const importType = STEP_IMPORT_TYPE_MAP[step.sheetName.toLowerCase()];
  const templateType = SHEET_TO_TEMPLATE_TYPE[step.sheetName.toLowerCase()] ?? step.sheetName;
  const templateUrl = IMPORT_TEMPLATE_URL(templateType);

  return (
    <div
      style={{
        width: CARD_WIDTH,
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: 8,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
        backgroundColor: "#fff",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div>
        <Text size={4} fontWeight="bold" display="block">
          {step.displayName}
        </Text>
        {step.dependsOn.length > 0 && (
          <Text size={2} color="default2" display="block">
            <FormattedMessage
              {...messages.dependsOn}
              values={{ deps: step.dependsOn.join(", ") }}
            />
          </Text>
        )}
      </div>

      {/* Field chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {step.fieldPreview.slice(0, 8).map(field => (
          <span
            key={field.name}
            style={{
              border: `1px solid ${field.required ? COLOR_ACCENT : COLOR_BORDER}`,
              borderRadius: 4,
              padding: "2px 6px",
              fontSize: 11,
              color: field.required ? COLOR_ACCENT : "#374151",
              whiteSpace: "nowrap",
            }}
          >
            {field.name}
            {field.required && " *"}
          </span>
        ))}
        {step.fieldPreview.length > 8 && (
          <span style={{ fontSize: 11, color: "#9ca3af", alignSelf: "center" }}>
            <FormattedMessage
              {...messages.moreFields}
              values={{ count: step.fieldPreview.length - 8 }}
            />
          </span>
        )}
      </div>

      {/* Actions */}
      {INFO_ONLY_SHEETS.has(step.sheetName) ? (
        // Info-only node: bundled inside another import type, no actions needed.
        <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>
          <FormattedMessage {...messages.bundledInVariants} />
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
          <Button
            variant="secondary"
            size="small"
            style={{ width: "100%" }}
            onClick={() => download(templateUrl, `template-${step.sheetName}.xlsx`)}
          >
            <FormattedMessage {...messages.downloadTemplate} />
          </Button>

          {/* Images-only: upload files, then scan the folder & generate a sheet */}
          {step.sheetName === "images" && (
            <>
              <div style={{ borderTop: `1px solid ${COLOR_BORDER}`, margin: "4px 0" }} />
              {/* Drag-and-drop images straight into the import folder */}
              <ImageUploadDropzone />
              <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.4 }}>
                <FormattedMessage {...messages.scanDescription} />
              </div>
              <Button
                variant="secondary"
                size="small"
                style={{ width: "100%", borderColor: COLOR_ACCENT, color: COLOR_ACCENT }}
                onClick={() => download(IMPORT_SCAN_IMAGES_URL(), "images_scanned.xlsx")}
              >
                <FormattedMessage {...messages.scanButton} />
              </Button>
            </>
          )}

          {importType && (
            <Button
              variant="primary"
              size="small"
              style={{ width: "100%" }}
              onClick={() => onImport(importType)}
            >
              <FormattedMessage {...messages.importButton} />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vertical down arrow (used inside the centre column)
// ---------------------------------------------------------------------------

const ARROW_V = 30; // height of the vertical arrow section

const VArrow = () => (
  <div style={{ display: "flex", justifyContent: "center", height: ARROW_V, flexShrink: 0 }}>
    <svg width={16} height={ARROW_V} style={{ display: "block" }}>
      <line x1={8} y1={2} x2={8} y2={ARROW_V - 8} stroke={COLOR_ARROW} strokeWidth={1.5} />
      <polyline
        points={`4,${ARROW_V - 12} 8,${ARROW_V - 8} 12,${ARROW_V - 12}`}
        fill="none"
        stroke={COLOR_ARROW}
        strokeWidth={1.5}
      />
    </svg>
  </div>
);

// ---------------------------------------------------------------------------
// Bracket connector: N inputs → 1 output  (left side: categories + prod_types → products)
// ---------------------------------------------------------------------------

const LeftBracket = ({
  topHeight,
  bottomHeight,
  midHeight: _midHeight,
}: {
  topHeight: number; // height of top card
  bottomHeight: number; // height of bottom card
  midHeight: number; // height of center card (products)
}) => {
  const totalH = topHeight + CARD_GAP + bottomHeight;
  const midY = topHeight + CARD_GAP / 2;
  const w = COL_GAP;

  return (
    <div style={{ width: w, height: totalH, flexShrink: 0, position: "relative" }}>
      <svg width={w} height={totalH} style={{ position: "absolute", top: 0, left: 0 }}>
        {/* Horizontal line from top card */}
        <line
          x1={0}
          y1={topHeight / 2}
          x2={w / 2}
          y2={topHeight / 2}
          stroke={COLOR_ARROW}
          strokeWidth={1.5}
        />
        {/* Horizontal line from bottom card */}
        <line
          x1={0}
          y1={topHeight + CARD_GAP + bottomHeight / 2}
          x2={w / 2}
          y2={topHeight + CARD_GAP + bottomHeight / 2}
          stroke={COLOR_ARROW}
          strokeWidth={1.5}
        />
        {/* Vertical connector between the two */}
        <line
          x1={w / 2}
          y1={topHeight / 2}
          x2={w / 2}
          y2={topHeight + CARD_GAP + bottomHeight / 2}
          stroke={COLOR_ARROW}
          strokeWidth={1.5}
        />
        {/* Horizontal line out to products, from vertical mid-point */}
        <line x1={w / 2} y1={midY} x2={w - 8} y2={midY} stroke={COLOR_ARROW} strokeWidth={1.5} />
        {/* Arrow head */}
        <polyline
          points={`${w - 12},${midY - 4} ${w - 8},${midY} ${w - 12},${midY + 4}`}
          fill="none"
          stroke={COLOR_ARROW}
          strokeWidth={1.5}
        />
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Bracket connector: 1 input → N outputs
// entryY: Y position (within totalH) where the horizontal input line arrives.
//         Defaults to the midpoint between top and bottom branch centres.
// ---------------------------------------------------------------------------

const RightBracket = ({ heights, entryY: entryYProp }: { heights: number[]; entryY?: number }) => {
  const totalH = heights.reduce((s, h) => s + h, 0) + CARD_GAP * (heights.length - 1);
  const w = COL_GAP;

  const centres: number[] = [];
  let y = 0;

  for (let i = 0; i < heights.length; i++) {
    centres.push(y + heights[i] / 2);
    y += heights[i] + CARD_GAP;
  }

  const topY = centres[0];
  const botY = centres[centres.length - 1];
  const entryY = entryYProp ?? (topY + botY) / 2;

  return (
    <div style={{ width: w, height: totalH, flexShrink: 0, position: "relative" }}>
      <svg width={w} height={totalH} style={{ position: "absolute", top: 0, left: 0 }}>
        <line x1={0} y1={entryY} x2={w / 2} y2={entryY} stroke={COLOR_ARROW} strokeWidth={1.5} />
        <line x1={w / 2} y1={topY} x2={w / 2} y2={botY} stroke={COLOR_ARROW} strokeWidth={1.5} />
        {centres.map((cy, i) => (
          <g key={i}>
            <line x1={w / 2} y1={cy} x2={w - 8} y2={cy} stroke={COLOR_ARROW} strokeWidth={1.5} />
            <polyline
              points={`${w - 12},${cy - 4} ${w - 8},${cy} ${w - 12},${cy + 4}`}
              fill="none"
              stroke={COLOR_ARROW}
              strokeWidth={1.5}
            />
          </g>
        ))}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

// Estimated card height — used to draw brackets before DOM measures.
// Cards are rendered in a hidden pass first, but for simplicity we use a
// fixed estimate: header + ~3 chip rows + 2 buttons ≈ 200px.
// The SVG connectors are sized to match.
const CARD_HEIGHT_ESTIMATE = 210;

export const ImportPipelineChain = ({ steps, onImport }: ImportPipelineChainProps) => {
  const { download } = useTemplateDownload();

  // Build a lookup so we can reference steps by sheet name
  const sm = Object.fromEntries(steps.map(s => [s.sheetName, s]));

  const renderCard = (sheetName: string) => {
    const step = sm[sheetName];

    if (!step) return <div style={{ width: CARD_WIDTH, height: CARD_HEIGHT_ESTIMATE }} />;

    return <Card step={step} onImport={onImport} download={download} />;
  };

  // ---------------------------------------------------------------------------
  // Layout:
  //
  //               ┌──────────────┐
  //               │ [Collections]│
  //               └──────┬───────┘
  // [Categories] ─┐      ↓              ┌──► [Channel Listings]
  //                ├──► [Products] ─────┤
  // [Prod Types] ─┘      ↓              └──► [Variants] ─┬─► [VCL]
  //               ┌──────┴───────────┐                   └─► [Images]
  //               │[Coll. Products]  │
  //               └──────────────────┘
  //
  // Products is a standalone card that the left bracket connects to directly.
  // Collections (above) and Collection Products (below) sit in grey boxes
  // flanking Products vertically in the centre column.
  //
  // Height maths:
  //   collBoxH   = GREY_PAD + h + GREY_PAD   (grey section height)
  //   vGap       = CARD_GAP/2 + ARROW_V + CARD_GAP/2   (space between box and Products)
  //   centreColH = collBoxH + vGap + h + vGap + collBoxH
  //   prodCentreY = collBoxH + vGap + h/2    (from centre-col top)
  //   leftColH   = h + CARD_GAP + h
  //   leftMT     = prodCentreY - leftColH/2
  //   rightColH  = h + CARD_GAP + (h + CARD_GAP + h)
  //   rightMT    = prodCentreY - rightColH/2
  // ---------------------------------------------------------------------------
  const h = CARD_HEIGHT_ESTIMATE;
  const GREY_PAD = 12;
  const collBoxH = GREY_PAD + h + GREY_PAD;
  const vGap = CARD_GAP / 2 + ARROW_V + CARD_GAP / 2;

  const prodCentreY = collBoxH + vGap + h / 2;

  const variantsChildH = h + CARD_GAP + h;
  // Right branches off Products: Channel Listings, Variants branch, Shipping Rates.
  const rightColH = h + CARD_GAP + variantsChildH + CARD_GAP + h;
  const rightMT = Math.round(prodCentreY - rightColH / 2);
  const rightBracketEntryY = prodCentreY - rightMT;

  const greyBox: React.CSSProperties = {
    backgroundColor: "rgba(243, 244, 246, 0.6)",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    padding: GREY_PAD,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
  };

  // ── Layout geometry ────────────────────────────────────────────────────────
  //
  //  Col A (attribute chain)         Col C (centre)              Col E (right)
  //  ┌──────────────────┐            ┌─────────────┐
  //  │   [Attributes]   │            │ [Collections]│
  //  │        ↓         │            └──────┬──────┘
  //  │  [Product Types] │─┐                 ↓             ┌──► [Channel Listings]
  //  │        ↓         │ ├──────────► [Products] ─────────┤
  //  │  [PT Attributes] │ │                 ↓             └──► [Variants] ─┬─► [VCL]
  //  └──────────────────┘ │          [Coll.Products]                        └─► [Images]
  //  [Categories] ────────┘
  //
  // Col A renders: Attributes → VArrow → Product Types → VArrow → PT Attributes
  // as a vertical chain. Its total height: attrChainH.
  // Col A bottom sibling: Categories (height h).
  // Both feed into Products via LeftBracket.

  const attrChainH = h * 3 + ARROW_V * 2; // Attributes + 2 arrows + Product Types + PT Attributes

  // LeftBracket: top = attribute chain, bottom = categories
  const newLeftColH = attrChainH + CARD_GAP + h;
  const newLeftMT = Math.round(prodCentreY - newLeftColH / 2);
  // When the left column is taller than the centre, newLeftMT is negative.
  // Add top padding so the column isn't clipped by the container.
  const topPad = Math.max(0, -newLeftMT) + 8;

  return (
    <div style={{ overflowX: "auto", width: "100%", paddingBottom: 12 }}>
      <div
        style={{
          width: "max-content",
          paddingTop: topPad,
          paddingBottom: 8,
          display: "flex",
          alignItems: "flex-start",
        }}
      >
        {/* ── Col A: Attribute chain + Categories ────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: CARD_GAP,
            marginTop: newLeftMT,
            flexShrink: 0,
          }}
        >
          {/* Attribute setup chain */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            {renderCard("attributes")}
            <VArrow />
            {renderCard("product_types")}
            <VArrow />
            {renderCard("product_type_attributes")}
          </div>

          {/* Categories sits below the chain, both feed into Products */}
          {renderCard("categories")}
        </div>

        {/* ── Col B: converging bracket ───────────────────────────────────── */}
        <div style={{ marginTop: newLeftMT, flexShrink: 0 }}>
          <LeftBracket topHeight={attrChainH} bottomHeight={h} midHeight={newLeftColH} />
        </div>

        {/* ── Col C: centre column — Collections / Products / Coll.Products  */}
        <div
          style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center" }}
        >
          {/* Collections grey box */}
          <div style={greyBox}>
            <div
              style={{
                fontSize: 10,
                color: "#9ca3af",
                fontStyle: "italic",
                alignSelf: "flex-start",
              }}
            >
              Optional Grouping
            </div>
            {renderCard("collections")}
          </div>

          {/* Down arrow Collections → Products */}
          <VArrow />

          {/* Products — the main connection node */}
          {renderCard("products")}

          {/* Down arrow Products → Collection Products */}
          <VArrow />

          {/* Collection Products grey box */}
          <div style={greyBox}>{renderCard("collection_products")}</div>
        </div>

        {/* ── Col D: diverging bracket → right branches ──────────────────── */}
        <div style={{ marginTop: rightMT, flexShrink: 0 }}>
          <RightBracket heights={[h, variantsChildH, h]} entryY={rightBracketEntryY} />
        </div>

        {/* ── Col E: right branches ──────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: CARD_GAP,
            marginTop: rightMT,
            flexShrink: 0,
          }}
        >
          {/* Branch 1: Channel Listings */}
          {renderCard("channel_listings")}

          {/* Branch 2: Variants → Variant Channel Listings + Images */}
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ height: variantsChildH, display: "flex", alignItems: "center" }}>
              {renderCard("variants")}
            </div>
            <RightBracket heights={[h, h]} />
            <div style={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
              {renderCard("variant_channel_listings")}
              {renderCard("images")}
            </div>
          </div>

          {/* Branch 3: Shipping Rates (terminal; uses products/types for exclusions) */}
          {renderCard("shipping_rates")}
        </div>
      </div>
    </div>
  );
};

export default ImportPipelineChain;
